<template>
  <div class="d-flex flex-column p-4 gap-4" style="min-height: 100vh; background: #f8f9fa;">
    <!-- Loading Skeleton -->
    <div v-if="isPageLoading" class="p-3">
      <div class="skeleton-label skeleton-animate mb-3"></div>
      <div class="d-flex gap-3 align-items-center flex-wrap mb-4">
        <div class="skeleton-radio skeleton-animate"></div>
        <div class="skeleton-radio skeleton-animate"></div>
        <div class="skeleton-radio skeleton-animate"></div>
      </div>
      <CardLoader />
    </div>

    <!-- Main Content -->
    <div v-else class="p-3 w-100">
      <ContentContainer>
        <template v-slot:content>
          <div class="">
            <div v-if="!isActivityWorkgroup && !isSkill && !isAdminsRoute">
              <label class="form-label fw-semibold">Send Message To</label>
              <div class="mb-4 d-flex gap-2 align-items-center">
                <template v-if="messagingCenterInfo && messagingCenterInfo.has_permission">
                  <material-radio v-for="button in messagingCenterInfo.buttons" :key="button.id" :id="button.id"
                    name="messageTarget" :checked="messageTarget === button.command"
                    @change="onMessageTargetChange(button.command)">
                    {{ button.label }}
                  </material-radio>
                </template>
                <template v-else>
                  <div class="text-danger">You do not have permission to use the messaging center.</div>
                </template>
              </div>
              <hr>
            </div>

            <div v-if="messageTarget === 'skillusers' && !isActivityWorkgroup"
              class="d-flex flex-column flex-md-row align-items-end mb-4 mt-4 gap-2">
              <div class="flex-fill">
                <label class="form-label fw-semibold">Select Skill</label>
                <div class="dropdown">
                  <button class="btn btn-outline-secondary dropdown-toggle w-100 text-start m-0" type="button"
                    @click="showSkillDropdown = !showSkillDropdown">
                    <span v-if="selectedSkills.length === 0" class="text-muted">
                      Select skill...
                    </span>
                    <span v-else>
                      {{ selectedSkills.length }} skill(s) selected
                    </span>
                  </button>

                  <div class="dropdown-menu w-100 p-3" :class="{ show: showSkillDropdown }"
                    style="max-height: 300px; overflow-y: auto;">
                    <div class="d-flex justify-content-between mb-2">
                      <button type="button" class="btn btn-sm btn-outline-primary" @click="selectAllSkills">
                        Select All
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="deselectAllSkills">
                        Deselect All
                      </button>
                    </div>

                    <div class="mb-2">
                      <input type="text" class="form-control form-control-sm p-2"
                        style="border: 1px solid #eee; border-radius: 5px;" placeholder="Search skills..."
                        v-model="skillSearch" />
                    </div>

                    <div class="form-check" v-for="skill in filteredSkills" :key="skill.skillid">
                      <input class="form-check-input" type="checkbox" :id="`skill-${skill.skillid}`" :value="skill"
                        v-model="selectedSkills" />
                      <label class="form-check-label" :for="`skill-${skill.skillid}`">
                        <div class="fw-medium" style="font-size: 12px;">{{ skill.skillname }}</div>
                      </label>
                    </div>
                  </div>
                </div>

                <div class="mt-2" v-if="selectedSkills.length > 0">
                  <span v-for="skill in selectedSkills" :key="skill.id" class="badge bg-secondary me-2 mb-2 p-2">
                    {{ skill.skillname }}
                    <button type="button" class="btn-close text-white ms-2" style="font-size: 0.7em;"
                      @click="removeSkill(skill)" />
                  </span>
                </div>
              </div>
            </div>

            <div v-if="isActivityWorkgroup" class="d-flex flex-column flex-md-row align-items-end mb-4 mt-4 gap-2">
              <div class="flex-fill">
                <label class="form-label fw-semibold">Select Activity Workgroup</label>
                <div class="dropdown">
                  <button class="btn btn-outline-secondary dropdown-toggle w-100 text-start m-0" type="button"
                    @click="showSkillDropdown = !showSkillDropdown">
                    <span v-if="selectedActivityWorkgroups.length === 0" class="text-muted">
                      Select Activity Workgroup...
                    </span>
                    <span v-else>
                      {{ selectedActivityWorkgroups.length }} workgroup(s) selected
                    </span>
                  </button>

                  <div class="dropdown-menu w-100 p-3" :class="{ show: showSkillDropdown }"
                    style="max-height: 300px; overflow-y: auto;">
                    <div class="d-flex justify-content-between mb-2">
                      <button type="button" class="btn btn-sm btn-outline-primary" @click="selectAllActivities">
                        Select All
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="deselectAllActivities">
                        Deselect All
                      </button>
                    </div>

                    <div class="mb-2">
                      <input type="text" class="form-control form-control-sm p-2"
                        style="border: 1px solid #eee; border-radius: 5px;" placeholder="Search workgroups..."
                        v-model="skillSearch" />
                    </div>

                    <div class="form-check" v-for="workgroup in activityWorkgroupOptions" :key="workgroup.value">
                      <input class="form-check-input" type="checkbox" :id="`workgroup-${workgroup.value}`"
                        :value="workgroup" v-model="selectedActivityWorkgroups" />
                      <label class="form-check-label" :for="`workgroup-${workgroup.value}`">
                        <div class="fw-medium" style="font-size: 12px;">{{ workgroup.label }}</div>
                      </label>
                    </div>
                  </div>
                </div>

                <div class="mt-2" v-if="selectedActivityWorkgroups.length > 0">
                  <span v-for="workgroup in selectedActivityWorkgroups" :key="workgroup.value"
                    class="badge bg-secondary me-2 mb-2 p-2">
                    {{ workgroup.label }}
                    <button type="button" class="btn-close text-white ms-2" style="font-size: 0.7em;"
                      @click="selectedActivityWorkgroups = selectedActivityWorkgroups.filter(s => s.value !== workgroup.value)" />
                  </span>
                </div>
              </div>
            </div>

            <!-- Recipients Section -->
            <div class="d-flex flex-column flex-md-row align-items-end mb-4 mt-2 gap-2">
              <div class="flex-fill">
                <label class="form-label fw-semibold">Recipients</label>
                <div class="dropdown">
                  <button class="btn btn-outline-secondary dropdown-toggle w-100 text-start m-0" type="button"
                    @click="showRecipientDropdown = !showRecipientDropdown">
                    <span v-if="selectedRecipients.length === 0" class="text-muted">
                      Select recipients...
                    </span>
                    <span v-else>
                      {{ selectedRecipients.length }} recipient(s) selected
                    </span>
                  </button>

                  <div class="dropdown-menu w-100 p-3" :class="{ show: showRecipientDropdown }"
                    style="max-height: 300px; overflow-y: auto;">
                    <div class="d-flex justify-content-between mb-2">
                      <button type="button" class="btn btn-sm btn-outline-primary" @click="selectAllRecipients">
                        Select All
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="deselectAllRecipients">
                        Deselect All
                      </button>
                    </div>

                    <div class="mb-2">
                      <select class="form-select form-select-sm mb-2" v-model="genderFilter">
                        <option value="">All Genders</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                      </select>
                      <input type="text" class="form-control form-control-sm p-2"
                        style="border: 1px solid #eee; border-radius: 5px;" placeholder="Search recipients..."
                        v-model="recipientSearch">
                    </div>

                    <div class="form-check" v-for="recipient in filteredRecipients" :key="recipient.id">
                      <input class="form-check-input" type="checkbox" :id="`recipient-${recipient.id}`"
                        :value="recipient" v-model="selectedRecipients">
                      <label class="form-check-label" :for="`recipient-${recipient.id}`">
                        <div class="fw-medium" style="font-size: 12px;">{{ recipient.name }}</div>
                        <small class="text-muted">{{ recipient.email }}{{ recipient.can_receive_text ? " [SMS] " : ""
                        }}</small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex-shrink-0" style="width:fit-content;">
                <MaterialButton class="btn btn-success text-white mb-1" :class="{ 'pulse-button': shouldPulseGetRecipients }"
                  style="max-height: fit-content;"
                  @click="getRecipients" :disabled="isGettingRecipients">
                  <span v-if="isGettingRecipients" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                  <i v-else class="material-icons me-2">group</i>
                  {{ isGettingRecipients ? 'Loading...' : 'Get Recipients' }}
                </MaterialButton>
              </div>
            </div>

            <div class="my-2" v-if="selectedRecipients.length > 0">
              <span v-for="recipient in selectedRecipients" :key="recipient.id" class="badge bg-info me-2 mb-2 p-2">
                {{ `${recipient.firstname} ${recipient.lastname}` }}
                <button type="button" class="btn-close text-white ms-2" style="font-size: 0.7em;"
                  @click="removeRecipient(recipient)"></button>
              </span>
            </div>

            <!-- Subject -->
            <div class="mb-4">
              <label for="subject" class="form-label fw-semibold">Subject</label>
              <material-input type="text" class="form-control" id="subject" v-model="subject"
                placeholder="Enter email subject..." />
            </div>

            <div class="d-flex justify-content-end gap-2">
              <button type="button"
                :class="isWelcomeMessage ? 'btn btn-info text-white welcome-tpl-pulse' : 'btn btn-outline-info'"
                @click="loadWelcomeTemplate" :disabled="isLoadingTemplate">
                <span v-if="isLoadingTemplate" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                <i v-else class="material-icons me-2">upload</i>
                {{ isLoadingTemplate ? 'Loading...' : 'Load Welcome Template' }}
              </button>
              <button type="button" class="btn btn-success text-white" @click="previewTokens"
                :disabled="isViewingTokens">
                <span v-if="isViewingTokens" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                <i v-else class="material-icons me-2">format_align_right</i>
                {{ isViewingTokens ? 'Loading...' : 'View Tokens' }}
              </button>
            </div>

            <!-- Content Tabs -->
            <div class="mb-4">
              <ul class="nav nav-tabs w-full" role="tablist">
                <li class="nav-item" role="presentation">
                  <button class="nav-link text-secondary d-flex align-items-center gap-1"
                    :class="{ active: activeTab === 'editor' }" @click="activeTab = 'editor'" type="button">
                    <i class="material-icons">text_snippet</i>
                    Rich Editor
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link text-secondary d-flex align-items-center gap-1"
                    :class="{ active: activeTab === 'html' }" @click="activeTab = 'html'" type="button">
                    <i class="material-icons">code</i>
                    HTML
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link text-secondary d-flex align-items-center gap-1"
                    :class="{ active: activeTab === 'plain' }" @click="activeTab = 'plain'" type="button">
                    <i class="material-icons">title</i>
                    Plain Text
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link text-secondary d-flex align-items-center gap-1"
                    :class="{ active: activeTab === 'sms' }" @click="activeTab = 'sms'" type="button">
                    <i class="material-icons">sms</i>
                    SMS Text
                  </button>
                </li>
              </ul>

              <div class="tab-content border border-top-0 p-3">
                <div v-if="activeTab === 'editor'" class="tab-pane fade show active">
                  <QuillEditor :key="editorKey" v-model:content="content.rich" contentType="html" theme="snow"
                    style="height: 300px;" />
                </div>

                <div v-if="activeTab === 'html'" class="tab-pane fade show active">
                  <textarea class="form-control font-monospace" rows="12" v-model="content.html"
                    placeholder="Enter HTML content..." style="font-size: 13px;"></textarea>
                </div>

                <div v-if="activeTab === 'plain'" class="tab-pane fade show active">
                  <textarea class="form-control" rows="12" v-model="content.plain"
                    placeholder="Enter plain text content..." readonly></textarea>
                </div>

                <div v-if="activeTab === 'sms'" class="tab-pane fade show active">
                  <textarea class="form-control" rows="12" v-model="content.sms"
                    placeholder="Enter sms text content..."></textarea>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex flex-wrap justify-content-end gap-2">
              <button type="button" class="btn btn-outline-secondary" @click="resetForm" :disabled="isResetting">
                <span v-if="isResetting" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                <i v-else class="material-icons me-2">restart_alt</i>
                {{ isResetting ? 'Resetting...' : 'Reset' }}
              </button>
              <button type="button" class="btn btn-outline-success" @click="previewEmail" :disabled="isPreviewing">
                <span v-if="isPreviewing" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                <i v-else class="material-icons me-2">visibility</i>
                {{ isPreviewing ? 'Loading...' : 'Preview' }}
              </button>

              <button v-if="activeTab != 'sms'" type="button" class="btn btn-success text-white" @click="sendEmail"
                :disabled="!canSend || isSending">
                <span v-if="isSending" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                <span v-else>Send Email</span>
                <i v-if="!isSending" class="material-icons ms-2">send</i>
              </button>

              <button v-else type="button" class="btn btn-primary text-white" @click="sendSms" :disabled="isSendingSms">
                <span v-if="isSendingSms" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                <i v-else class="material-icons me-2">send</i>
                {{ isSendingSms ? 'Sending...' : 'Send via SMS' }}
              </button>

            </div>

            <div class="d-flex flex-wrap justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-success text-white" @click="openLocalEmailClient"
                :disabled="isOpeningEmailClient">
                <span v-if="isOpeningEmailClient" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                <i v-else class="material-icons me-2">email</i>
                {{ isOpeningEmailClient ? 'Opening...' : 'Send Email through Local e-mail client' }}
              </button>
            </div>

            <!-- <div class="my-4 text-sm text-info">
              ** Sending and receiving SMS messages is handled via a 3rd party provider that charges
              churchscheduling.com for each message sent/received.
              As a result, the ability to send mass SMS messages on-demand is currently only enabled for
              Organizations who donate the annual minimum of $100.
              Please contact us (using request assistance button) if you have any questions.
            </div> -->
          </div>

          <!-- Preview Modal -->
          <ContentModal v-model="showPreview" title="Email Preview" size="lg">
            <template #default>
              <div class="mb-3">
                <strong>To:</strong>
                <span v-for="(recipient, index) in selectedRecipients" :key="recipient.id">
                  {{ recipient.email }}<span v-if="index < selectedRecipients.length - 1">, </span>
                </span>
              </div>

              <div class="mb-3">
                <strong>Subject:</strong> {{ subject || 'No subject' }}
              </div>

              <div class="border p-3 bg-light">
                <div v-if="activeTab === 'editor'" v-html="content.rich"></div>
                <div v-else-if="activeTab === 'html'" v-html="content.html"></div>
                <pre v-else class="mb-0">{{ content.plain }}</pre>
              </div>
            </template>
          </ContentModal>

          <!-- Tokens Modal -->
          <ContentModal v-model="showTokens" title="Token/Variables">
            <template #default>
              <div v-pre>
                <b>{{firstname}}</b> – replaced with user's firstname <br>
                <b>{{lastname}}</b> – replaced with user's lastname <br>
                <b>{{email}}</b> – replaced with user's email address <br>
                <b>{{ical}}</b> – replaced with user's iCal subscription URL <br>
                <b>{{signup}}</b> – replaced with list of Sign-up only assignments for the next 60 days (email only).
                <br>
                <b>{{preferences}}</b> – replaced with user Availability, Preferences, Scheduling rules.
              </div>
            </template>
          </ContentModal>

        </template>
      </ContentContainer>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import MaterialInput from '../../components/common/MaterialInput.vue'
import MaterialRadio from "@/components/common/MaterialRadio.vue"
import MaterialButton from "@/components/common/MaterialButton.vue"
import ContentContainer from "@/components/PageLayout/ContentContainer.vue"
import ContentModal from '../../components/Modal/ContentModal.vue'
import CardLoader from '../../components/Loader/CardLoader.vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { getLogginedPersonId } from '@/utils/SessionDataHandler'

/* Loading state trackers - separate variable for each action */
const isPageLoading = ref(true)
const isGettingRecipients = ref(false)
const isViewingTokens = ref(false)
const isResetting = ref(false)
const isLoadingTemplate = ref(false)
const isPreviewing = ref(false)
const isSending = ref(false)
const isOpeningEmailClient = ref(false)
const isSendingSms = ref(false)
const isWelcomeMessage = ref(false)
const welcomeTemplateData = ref(null)

/* Reactive data */
const showRecipientDropdown = ref(false)
const recipientSearch = ref('')
const selectedRecipients = ref([])
const subject = ref('')
const activeTab = ref('editor')
const showPreview = ref(false)
const showTokens = ref(false)
const messageTarget = ref("admins")
const store = useStore();
const route = useRoute();
const locationId = route.params.locationid;
const source = route.params.source;
const activityWorkgroupId = route.params.activityworkgroupid || '';
const selectedRecord = computed(() => {
  const query = route.query;
  return query.selectedRecord || query.selectedrecord || null;
});
const isActivityWorkgroup = computed(() => source == 'activity-workgroup' && activityWorkgroupId != '')
const isSkill = computed(() => source == 'skill')
const isAdminsRoute = computed(() => route.name === 'messaging-center-admins' || source === 'admins')


const showSkillDropdown = ref(false)
const skillSearch = ref('')
const selectedSkills = ref([])
const selectedActivityWorkgroups = ref([])
const genderFilter = ref('');
const messagingCenterInfo = computed(() => store.getters.stMessagingCenterInfo);
const toast = useToast();

const initialSubjectText = `Launching new volunteer system at {{organizationname}} - {{locationname}}`;

const initialWelcomeHtml = `<p>Hi <strong>{{firstname}}</strong>,</p>
<p>Welcome to <strong>ChurchScheduling.com</strong>!</p>
<p>ChurchScheduling.com is a cloud-based platform, fully integrated with AI tools, that is designed to help organizations schedule team members, organize services, and streamline communication.</p>
<p>Your organization has just launched this new system to give you more input into your volunteer schedule.</p>
<h3>Getting Started</h3>
<p>Your user ID is: <strong>{{email}}</strong></p>
<p>On your initial log in, you will need to use the <strong>reset password</strong> option to generate a new password. <br>
Your user ID (<em>{{email}}</em>) and your first name (<em>{{firstname}}</em>) are required for password reset. The generated password will be emailed to you and can be changed on your first logon via the <em>Change my Password</em> page.</p>
<h3>How It Works</h3>
<p>ChurchScheduling.com is an automated scheduling system. Your team admins can generate schedules automatically, and you will have control over when and how often you are scheduled. <br>
The system can also automatically find substitutes for any assignments that are declined.</p>
<h3>Features Available to You</h3>
<ul>
<li>Control how often you want to be scheduled (weekly, monthly, etc.)</li>
<li>Swap assignments with other members of your team</li>
<li>Set preferences for working with others</li>
<li>Enter dates (specific or recurring) when you are available or unavailable</li>
<li>Set the Activities/Services you prefer to work</li>
<li>View assignments in external calendars (Google, Yahoo) with your custom iCal feed URL: {{ical}}</li>
</ul>
<h3>SMS/Text Features</h3>
<p>If your mobile number has been entered into the system, you can also:</p>
<ul>
<li>Check your upcoming assignment by texting <strong>SCHEDULE</strong> to the notification number</li>
<li>Confirm or decline assignments by responding to text reminders with <strong>YES</strong> or <strong>NO</strong></li>
<li>Disable text reminders by texting <strong>STOP</strong> to the notification number</li>
</ul>
<h3>Your Preferences</h3>
<p>You can update your Availability dates, Preferred Activities and Workgroups, and Scheduling preferences by clicking the <em>my Profile/Preferences</em> button upon login.</p>
<p>Your current scheduling preferences are listed below:</p>
<p>{{preferences}}</p>
<hr>
<p>Please contact <a href="mailto:support@churchscheduling.com">support@churchscheduling.com</a> if you have any questions.</p>
<p>We are continually adding more features and updating the application — your feedback is greatly appreciated!</p>
<p><a href="https://www.churchscheduling.com">www.churchscheduling.com</a></p>`

const content = ref({
  rich: '',
  html: '',
  plain: '',
  sms: ''
})

const editorKey = ref(0)

// const recipients = ref([
//   {
//     id: 1,
//     name: 'John Smith',
//     email: 'john.smith@example.com',
//     avatar: '/placeholder.svg?height=32&width=32'
//   },
//   {
//     id: 2,
//     name: 'Sarah Johnson',
//     email: 'sarah.johnson@example.com',
//     avatar: '/placeholder.svg?height=32&width=32'
//   },
//   {
//     id: 3,
//     name: 'Mike Davis',
//     email: 'mike.davis@example.com',
//     avatar: '/placeholder.svg?height=32&width=32'
//   },
//   {
//     id: 4,
//     name: 'Emily Wilson',
//     email: 'emily.wilson@example.com',
//     avatar: '/placeholder.svg?height=32&width=32'
//   },
//   {
//     id: 5,
//     name: 'David Brown',
//     email: 'david.brown@example.com',
//     avatar: '/placeholder.svg?height=32&width=32'
//   }
// ])

var recipients = computed(() => {
  const persons = store.getters.stMessagingPersons?.persons || [];
  // Ensure recipients have a name property for display and search
  return persons.map(person => ({
    ...person,
    name: person.name || `${person.firstname || ''} ${person.lastname || ''}`.trim() || 'Unknown'
  }));
})
var skills = computed(() => store.getters.skillsFromManagedLocation)
var activityWorkgroupOptions = computed(() => {
  const allOptions = store.getters.stAIPageData.primary_list || [];
  // If selectedRecord is provided and not empty, filter to only show that record
  const recordValue = selectedRecord.value;

  // Only filter if we have a valid selectedRecord value
  if (recordValue && recordValue !== '' && recordValue !== 'null' && String(recordValue).trim() !== '') {
    const filtered = allOptions.filter(option => String(option.value) === String(recordValue));
    // If filter found a match, return it; otherwise return all (fallback)
    return filtered.length > 0 ? filtered : allOptions;
  }

  // No selectedRecord or it's empty/null - show all records
  return allOptions;
})

// const skills = ref([
//   {
//     id: 1,
//     name: 'Choir'
//   },
//   {
//     id: 2,
//     name: 'Usher'
//   },
//   {
//     id: 3,
//     name: 'Audio/Visual'
//   },
//   {
//     id: 4,
//     name: 'Hospitality'
//   },
//   {
//     id: 5,
//     name: 'Children Ministry'
//   }
// ])


/* Computed properties */
var filteredRecipients = computed(() => {
  let list = recipients.value || [];
  if (!Array.isArray(list)) return [];
  
  if (genderFilter.value) {
    list = list.filter(r => (r.gender || '') == genderFilter.value);
  }
  if (!recipientSearch.value) return list;
  
  const searchTerm = recipientSearch.value.toLowerCase();
  return list.filter(recipient => {
    const name = (recipient.name || `${recipient.firstname || ''} ${recipient.lastname || ''}`.trim() || '').toLowerCase();
    const email = (recipient.email || '').toLowerCase();
    return name.includes(searchTerm) || email.includes(searchTerm);
  });
})

var filteredSkills = computed(() => {
  if (!skillSearch.value) return skills.value
  return skills.value.filter(skill =>
    (skill.skillname || '').toLowerCase().includes(skillSearch.value.toLowerCase())
  )
})

const canSend = computed(() => {
  return selectedRecipients.value.length > 0 && subject.value.trim() && (content.value.rich || content.value.html || content.value.plain || content.value.sms)
})

const shouldPulseGetRecipients = computed(() => {
  // Pulse when a skill is selected
  if (messageTarget.value === 'skillusers' && selectedSkills.value.length > 0) {
    return true;
  }
  // Pulse when "send message to all members" or "send message to all admins" is selected
  // Keep pulsing until at least one recipient is selected (to remind user to select recipients)
  // Exclude skillusers and activity workgroup (aiwg)
  if (messageTarget.value !== 'skillusers' && messageTarget.value !== 'aiwg') {
    // Pulse if no recipients have been selected yet
    // This will pulse immediately when option is selected and continue until user selects recipients
    return selectedRecipients.value.length === 0;
  }
  return false;
})

/* Helpers */
const htmlToText = (html) => {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  div.querySelectorAll('br').forEach(() => div.appendChild(document.createTextNode('\n')))
  return div.textContent || div.innerText || ''
}

const getCurrentBodyText = () => {
  if (activeTab.value === 'plain') return content.value.plain || ''
  if (activeTab.value === 'html') return htmlToText(content.value.html)
  if (activeTab.value === 'sms') return htmlToText(content.value.sms) || ''
  return htmlToText(content.value.rich)
}

// Returns HTML content for rich text emails
const getMessageContent = () => {
  if (activeTab.value === 'html') return content.value.html || ''
  if (activeTab.value === 'plain') return content.value.plain || ''
  return content.value.rich || ''
}

/* Methods with loading states */
const getRecipients = async () => {
  isGettingRecipients.value = true;
  try {
    if (messageTarget.value === 'newusers') {
      await fetchNewUsers();
      return;
    }
    if (messageTarget.value === 'skillusers') {
      if (selectedSkills.value.length > 0) {
        await store.dispatch("fetchMessagingPersons", { messageTarget: messageTarget.value, idsArray: selectedSkills.value.map(r => r.skillid) });
      }
    }
    else if (isActivityWorkgroup.value) {
      // Fall back to ALL workgroups in this activity instance if the user
      // has not (or cannot) pre-select a specific workgroup.
      let ids = selectedActivityWorkgroups.value.map(r => r.value);
      if (ids.length === 0) {
        const allOptions = store.getters.stAIPageData.primary_list || [];
        ids = allOptions.map(r => r.value);
        if (allOptions.length > 0) {
          selectedActivityWorkgroups.value = [...allOptions];
        }
      }
      if (ids.length > 0) {
        await store.dispatch("fetchMessagingPersons", { messageTarget: "aiwg", idsArray: ids });
      } else {
        toast.warning('No activity workgroups available to load recipients from.', { position: 'top-right' });
      }
    }
    else {
      await store.dispatch("fetchMessagingPersons", { messageTarget: messageTarget.value, idsArray: [locationId] });
    }
  } catch (error) {
    console.error('getRecipients error', error);
    const msg = error?.response?.data?.message || 'Failed to load recipients.';
    toast.error(msg, { position: 'top-right' });
  } finally {
    isGettingRecipients.value = false;
  }
}

const removeRecipient = (recipient) => {
  const index = selectedRecipients.value.findIndex(r => r.id === recipient.id)
  if (index > -1) {
    selectedRecipients.value.splice(index, 1)
  }
}

const openLocalEmailClient = () => {
  if (selectedRecipients.value.length === 0) {
    alert('Please select at least one recipient.')
    return
  }

  const to = selectedRecipients.value.map(r => r.email).join(',')
  const subj = subject.value?.trim() || ''
  const body = getCurrentBodyText()
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`
  console.log("mailto", mailto);
  window.location.href = mailto
}

const clearRecipients = () => {
  selectedRecipients.value = []
  selectedSkills.value = [];
  selectedActivityWorkgroups.value = [];
  showRecipientDropdown.value = false
  showSkillDropdown.value = false
  // Keep subject and content so users can reuse the same message for different recipients
  // Note: isWelcomeMessage is intentionally NOT cleared here — it persists until the user
  // switches to a different message target.
}

const resetForm = () => {
  selectedRecipients.value = []
  selectedSkills.value = [];
  selectedActivityWorkgroups.value = [];
  subject.value = ''
  content.value = {
    rich: '',
    html: '',
    plain: '',
    sms: ''
  }
  activeTab.value = 'editor'
  showRecipientDropdown.value = false
  showSkillDropdown.value = false
  editorKey.value++
}

// Sanitise HTML so Quill can parse lists correctly.
// Browsers auto-close <p> before block elements like <ul>/<ol>, which
// causes Quill's clipboard converter to drop the list entirely.
const sanitizeHtmlForQuill = (html) => {
  if (!html) return html;
  // Remove <p> that directly wraps a <ul> or <ol>  (e.g. <p><ul>...</ul></p>)
  return html
    .replace(/<p>\s*(<[uo]l)/gi, '$1')
    .replace(/(<\/[uo]l>)\s*<\/p>/gi, '$1');
}

const loadWelcomeTemplate = () => {
  isLoadingTemplate.value = true;
  try {
    // Use API-provided welcome template if available, otherwise fall back to defaults
    const tpl = welcomeTemplateData.value;
    const subjectText = tpl?.subject || initialSubjectText;
    const bodyHtml = sanitizeHtmlForQuill(tpl?.body || initialWelcomeHtml);
    subject.value = subjectText;
    content.value.rich = bodyHtml;
    content.value.html = bodyHtml;
    content.value.plain = bodyHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    editorKey.value++;
    // Ensure welcome flag is set when template is loaded
    if (isWelcomeMessage.value || messageTarget.value === 'newusers') {
      isWelcomeMessage.value = true;
    }
  } finally {
    isLoadingTemplate.value = false;
  }
}

const previewEmail = () => {
  showPreview.value = true
}

const previewTokens = () => {
  showTokens.value = true
}

const sendEmail = async () => {
  if (!canSend.value) return
  isSending.value = true;

  const payload = {
    messageTarget: isActivityWorkgroup.value ? "ai" : messageTarget.value,
    locationid: locationId,
    messagetype: "email",
    message: getMessageContent(),
    messagesubject: subject.value,
    useridsarray: selectedRecipients.value.map(r => r.id),
    sender_id: getLogginedPersonId(),
  };

  // Include welcome message flag so backend marks recipients as having received welcome email
  if (isWelcomeMessage.value) {
    payload.is_welcome_message = true;
  }

  try {
    const response = await store.dispatch("sendMessage", payload);
    toast.success(response?.message || "Message queued for sending", { position: 'top-right' });
    clearRecipients(); // Only clear recipients, keep subject and content for reuse

    // Refresh the new users list after sending welcome messages
    if (isWelcomeMessage.value) {
      await fetchNewUsers();
    }
  } catch (error) {
    const message = error?.response?.data?.message || "Sending the email failed, please try again.";
    toast.error(message, { position: 'top-right' });
    console.error("sendEmail error", error);
  } finally {
    isSending.value = false;
  }
}

const sendSms = async () => {
  isSendingSms.value = true;

  const payload = {
    locationid: locationId,
    messagetype: "sms",
    message: getCurrentBodyText(),
    messagesubject: subject.value,
    useridsarray: recipients.value.filter(r => r.can_receive_text).map(r => r.id),
    sender_id: getLogginedPersonId(),
  };

  try {
    const response = await store.dispatch("sendMessage", payload);
    toast.success(response?.message || "SMS queued for sending", { position: 'top-right' });
    clearRecipients(); // Only clear recipients, keep subject and content for reuse
  } catch (error) {
    const message = error?.response?.data?.message || "Sending the SMS failed, please try again.";
    toast.error(message, { position: 'top-right' });
    console.error("sendSms error", error);
  } finally {
    isSendingSms.value = false;
  }
}

const removeSkill = (skill) => {
  const index = selectedSkills.value.findIndex(s => s.id === skill.id)
  if (index > -1) {
    selectedSkills.value.splice(index, 1)
  }
}

const selectAllSkills = () => {
  selectedSkills.value = [...skills.value]
}

const deselectAllSkills = () => {
  selectedSkills.value = []
}

const selectAllRecipients = () => {
  selectedRecipients.value = [...recipients.value]
}

const deselectAllRecipients = () => {
  selectedRecipients.value = []
}

const selectAllActivities = () => {
  selectedActivityWorkgroups.value = [...activityWorkgroupOptions.value]
}

const deselectAllActivities = () => {
  selectedActivityWorkgroups.value = []
}

/* Close dropdown when clicking outside */
const closeDropdowns = (e) => {
  if (!e.target.closest('.dropdown')) {
    showRecipientDropdown.value = false
    showSkillDropdown.value = false
  }
}

const onMessageTargetChange = async (command) => {
  messageTarget.value = command;
  selectedRecipients.value = [];
  recipients.value = [];
  filteredRecipients.value = [];
  selectedSkills.value = [];
  isWelcomeMessage.value = false;
  welcomeTemplateData.value = null;
  store.commit('setMessagingPersons', { persons: [] });

  if (command === 'newusers') {
    await fetchNewUsers();
  } else if (messageTarget.value == 'skillusers') {
    await store.dispatch("getSkillsFromManageLocation", { locationId });
  } else {
    getRecipients();
  }
}

const fetchNewUsers = async () => {
  isGettingRecipients.value = true;
  try {
    const data = await store.dispatch("fetchNewUsersData", locationId);
    if (data?.is_welcome_message) {
      isWelcomeMessage.value = true;
      welcomeTemplateData.value = data.welcome_template || null;
    }
    // Pre-select all recipients (intent is to message all new users)
    selectedRecipients.value = [...(recipients.value || [])];
  } catch (error) {
    toast.error('Failed to load new users list', { position: 'top-right' });
  } finally {
    isGettingRecipients.value = false;
  }
}

/* Initialize page */
onMounted(async () => {
  document.addEventListener('click', closeDropdowns)
  await store.dispatch("fetchMessagingCenterInfo", locationId);

  if (isAdminsRoute.value) {
    messageTarget.value = "locadmins";
    // Automatically get recipients for admins
    await store.dispatch("fetchMessagingPersons", { messageTarget: "locadmins", idsArray: [locationId] });
  }
  else if (isSkill.value) {
    messageTarget.value = messagingCenterInfo.value.buttons[2].command;
  }
  else {
    messageTarget.value = messagingCenterInfo.value.buttons[0].command;
  }

  // if (messageTarget.value != 'skillusers' && !isActivityWorkgroup.value) {
  //   await store.dispatch("fetchMessagingPersons", { messageTarget: messageTarget.value, idsArray: [...locationId] });
  // }
  // else {
  //   if (isActivityWorkgroup.value) {
  //     await store.dispatch("fetchActivityInstances", { locationId, activityWorkgroupId });
  //   }
  //   await store.dispatch("getSkillsFromManageLocation", locationId);
  // }

  if (messageTarget.value == 'skillusers' && !isActivityWorkgroup.value) {
    await store.dispatch("getSkillsFromManageLocation", { locationId });
  }
  else if (isActivityWorkgroup.value) {
    await store.dispatch("fetchActivityInstances", { locationId, activityWorkgroupId });

    // If selectedRecord is provided and matches a workgroup, pre-select just
    // that workgroup. Otherwise (e.g. 'Send Notifications' from the Activity
    // Instance menu where selectedRecord is the instance id and will not
    // match any workgroup's ai_workgroup_id), pre-select ALL workgroups so
    // admins can send to the entire instance.
    const recordValue = selectedRecord.value;
    const allOptions = store.getters.stAIPageData.primary_list || [];
    let workgroupToSelect = null;
    if (recordValue && recordValue !== '' && recordValue !== 'null' && String(recordValue).trim() !== '') {
      workgroupToSelect = allOptions.find(
        option => String(option.value) === String(recordValue)
      );
    }
    if (workgroupToSelect) {
      selectedActivityWorkgroups.value = [workgroupToSelect];
    } else if (allOptions.length > 0) {
      // Either no selectedRecord, or selectedRecord did not match any
      // workgroup -- fall back to selecting all workgroups.
      selectedActivityWorkgroups.value = [...allOptions];
    }

    // Pre-load recipients for the selected workgroup(s) so the user can
    // immediately start picking recipients without having to click
    // 'Get Recipients' first.
    const preloadIds = selectedActivityWorkgroups.value.map(r => r.value);
    if (preloadIds.length > 0) {
      try {
        await store.dispatch("fetchMessagingPersons", {
          messageTarget: "aiwg",
          idsArray: preloadIds,
        });
      } catch (error) {
        console.error('Error pre-loading messaging recipients:', error);
      }
    }
  }
  else {
    await store.dispatch("fetchMessagingPersons", { messageTarget: messageTarget.value, idsArray: [locationId] });
  }

  isPageLoading.value = false
})

// Watch for when activity workgroup options are loaded and selectedRecord is available
watch(
  [() => store.getters.stAIPageData.primary_list, selectedRecord],
  ([primaryList, recordValue]) => {
    // Only auto-select if we have a valid selectedRecord value
    if (isActivityWorkgroup.value && recordValue && recordValue !== '' && recordValue !== 'null' && String(recordValue).trim() !== '') {
      const allOptions = primaryList || [];
      const workgroupToSelect = allOptions.find(
        option => String(option.value) === String(recordValue)
      );
      if (workgroupToSelect && selectedActivityWorkgroups.value.length === 0) {
        selectedActivityWorkgroups.value = [workgroupToSelect];
      }
    }
    // If selectedRecord is empty/null, don't auto-select anything (user can select manually)
  },
  { immediate: true }
)

// Sync content between tabs when switching
watch(activeTab, (newTab, oldTab) => {
  // When switching to HTML tab, copy rich content to HTML
  if (newTab === 'html' && oldTab === 'editor') {
    content.value.html = content.value.rich || '';
  }
  // When switching to plain text tab, strip HTML from rich/html content
  if (newTab === 'plain') {
    const sourceHtml = content.value.rich || content.value.html || '';
    content.value.plain = htmlToText(sourceHtml);
  }
  // When switching from HTML tab back to editor, sync HTML changes to rich
  if (newTab === 'editor' && oldTab === 'html') {
    content.value.rich = content.value.html || '';
    editorKey.value++; // Force editor to re-render with new content
  }
})
</script>

<style scoped>
.dropdown-toggle::after {
  content: '' !important;
}

.dropdown-menu.show {
  display: block;
}

.btn-toolbar .btn-group {
  margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
  .btn-toolbar {
    flex-direction: column;
  }

  .btn-toolbar .btn-group {
    margin-right: 0 !important;
    margin-bottom: 0.5rem;
  }
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.5;
}

.modal.show {
  z-index: 1050;
}

/* Skeleton animation */
.skeleton-animate {
  animation: skeleton-loading 1.5s infinite;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.skeleton-label {
  width: 160px;
  height: 16px;
  border-radius: 4px;
}

.skeleton-radio {
  width: 220px;
  height: 20px;
  border-radius: 6px;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.9),
                0 0 0 0 rgba(76, 175, 80, 0.7),
                0 0 0 0 rgba(76, 175, 80, 0.5);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(76, 175, 80, 0),
                0 0 0 16px rgba(76, 175, 80, 0),
                0 0 0 24px rgba(76, 175, 80, 0);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0),
                0 0 0 0 rgba(76, 175, 80, 0),
                0 0 0 0 rgba(76, 175, 80, 0);
    transform: scale(1);
  }
}

.pulse-button {
  animation: pulse 1.5s infinite;
  position: relative;
}

@keyframes welcome-tpl-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(13, 202, 240, 0.7); }
  70%  { box-shadow: 0 0 0 10px rgba(13, 202, 240, 0); }
  100% { box-shadow: 0 0 0 0 rgba(13, 202, 240, 0); }
}
.welcome-tpl-pulse {
  animation: welcome-tpl-pulse 1.5s infinite;
}
</style>

<!-- Unscoped: ensure Quill editor renders list bullets -->
<style>
.ql-editor ul,
.ql-editor ol {
  padding-left: 1.5em !important;
}
.ql-editor ul > li {
  list-style-type: none;
}
.ql-editor ul > li::before {
  content: '\2022' !important;
  display: inline-block !important;
  white-space: nowrap;
  width: 1.2em;
  margin-left: -1.5em;
  margin-right: 0.3em;
  text-align: right;
}
.ql-editor ol > li::before {
  display: inline-block !important;
  white-space: nowrap;
  width: 1.2em;
  margin-left: -1.5em;
  margin-right: 0.3em;
  text-align: right;
}
</style>
